import { Stack } from '@mui/material';
import { ConversationParticipantEntity } from '@provision/conversation-types';
import { useEffect, useMemo } from 'react';
import { FormAutocompleteProps, FormField, FormLabel } from 'src/components';
import { SidePanelRow } from 'src/components/SidePanelRow';
import {
  Conversation,
  ConversationStatusOption,
  conversationTypeOptions,
  DEFAULT_STATUS,
  getPreparedConversationParticipantOptionGroup,
  PreparedConversationParticipantOption,
  useConversationStatusOptions,
  useConversationUrgencyOptions,
} from 'src/graphql';
import { useConversationUpdate } from 'src/graphql/entities/conversation/use-conversation-update';
import {
  ConversationAssigneeOption,
  useConversationAssigneeOptions,
} from 'src/graphql/entities/platform-user';
import { appendDisabledUserTextToName } from 'src/graphql/entities/platform-user/utils';
import { useSelectedConversationRequesterStore } from 'src/store/selected-conversation-requester';
import { ConversationParticipantRaw } from 'src/types';
import { ConversationAssigneeToggle } from './conversation-assignee-toggle';
import { ConversationRequesterToggle } from './conversation-requester-toggle';
import { ConversationStatusToggle } from './conversation-status-toggle';
import { ConversationTypeToggle } from './conversation-type-toggle';
import { ConversationUrgencyToggle } from './conversation-urgency-toggle';

export interface ConversationDetailsFormValues {
  conversationStatus: ConversationStatusOption;
  assignee: ConversationAssigneeOption | null;
  group: { id: string; label: string };
  type: { id: string; label: string };
  urgency: { id: string; label: string };
  requester: { id: string; label: string; participantEntity: ConversationParticipantEntity };
}

interface GetDefaultConversationParticipantOptionInput {
  requester: ConversationParticipantRaw;
  participantEntity?: ConversationParticipantEntity;
  participantIdentity?: string;
  conversationParticipantId?: string;
  fullName?: string;
  phoneNumber?: string;
  email?: string;
}

export function useConversationPropertiesForm(
  conversation: Conversation,
  shouldDisableFormFields?: boolean,
) {
  const { updateConversation } = useConversationUpdate();

  const urgencyOptions = useConversationUrgencyOptions(conversation.partner?.id);

  const { selectedAssignee, assigneeOptions } = useConversationAssigneeOptions(conversation);
  const { selectedConversationRequester, setPartnerSelectedConversationRequester } =
    useSelectedConversationRequesterStore((state) => state);

  const conversationParticipantOption = useMemo(() => {
    return (
      selectedConversationRequester ||
      getDefaultConversationParticipantOption({
        requester: conversation.requester,
        participantIdentity: conversation.requesterData?.participantIdentity,
        participantEntity: conversation.requesterData?.participantEntity,
        conversationParticipantId: conversation.requesterData?.conversationParticipantId,
        fullName: conversation.requesterFullName,
        email: conversation.requesterData?.email,
        phoneNumber: conversation.requesterData?.phoneNumber,
      })
    );
  }, [
    selectedConversationRequester,
    conversation.requester,
    conversation.requesterData?.participantIdentity,
    conversation.requesterData?.participantEntity,
    conversation.requesterData?.conversationParticipantId,
    conversation.requesterData?.email,
    conversation.requesterData?.phoneNumber,
    conversation.requesterFullName,
  ]);

  useEffect(
    () => setPartnerSelectedConversationRequester(null),
    [setPartnerSelectedConversationRequester, conversation.requesterId],
  );

  const statusOptions = useConversationStatusOptions();

  const conversationPropertiesFormFields = useMemo<FormField[]>(() => {
    const conversationStatus =
      statusOptions.find((item) => item.id === conversation.conversationStatusId) ||
      DEFAULT_STATUS;
    return [
      {
        name: 'conversationStatus',
        label: 'Status',
        defaultValue: conversationStatus,
        renderField(field: FormAutocompleteProps) {
          return (
            <SidePanelRow key={field.label}>
              <FormLabel label={field.label} />
              <Stack direction="row" width="100%" gap={1}>
                <ConversationStatusToggle
                  name={field.name}
                  disabled={shouldDisableFormFields}
                />
              </Stack>
            </SidePanelRow>
          );
        },
      },
      {
        name: 'assignee',
        label: 'Assignee',
        defaultValue: selectedAssignee ?? null,
        renderField(field: FormAutocompleteProps) {
          return (
            <SidePanelRow key={field.label}>
              <FormLabel label={field.label} />
              <Stack direction="row" width="100%" gap={1}>
                <ConversationAssigneeToggle
                  name={field.name}
                  options={assigneeOptions}
                  disabled={shouldDisableFormFields}
                />
              </Stack>
            </SidePanelRow>
          );
        },
      },
      {
        name: 'requester',
        label: 'Requester',
        defaultValue: conversationParticipantOption,
        renderField(field: FormAutocompleteProps) {
          return (
            <SidePanelRow key={field.label}>
              <FormLabel label={field.label} />
              <Stack direction="row" width="100%" gap={1}>
                <ConversationRequesterToggle
                  name={field.name}
                  partnerId={conversation.partnerId}
                  conversationId={conversation.conversationId}
                  disabled={shouldDisableFormFields}
                />
              </Stack>
            </SidePanelRow>
          );
        },
      },
      {
        name: 'type',
        label: 'Type',
        defaultValue:
          conversationTypeOptions.find((item) => item.id === conversation.type) || null,
        renderField(field: FormAutocompleteProps) {
          return (
            <SidePanelRow key={field.label}>
              <FormLabel label={field.label} />
              <Stack direction="row" width="100%" gap={1}>
                <ConversationTypeToggle name={field.name} disabled={shouldDisableFormFields} />
              </Stack>
            </SidePanelRow>
          );
        },
      },
      {
        name: 'urgency',
        label: 'Urgency',
        defaultValue: urgencyOptions.find((item) => item.id === conversation.urgency) || null,
        renderField(field: FormAutocompleteProps) {
          return (
            <SidePanelRow key={field.label}>
              <FormLabel label={field.label} />
              <Stack direction="row" width="100%" gap={1}>
                <ConversationUrgencyToggle
                  name={field.name}
                  partnerId={conversation.partner?.id}
                  disabled={shouldDisableFormFields}
                />
              </Stack>
            </SidePanelRow>
          );
        },
      },
    ] as (FormAutocompleteProps & { iconColor?: string })[];
  }, [
    shouldDisableFormFields,
    statusOptions,
    selectedAssignee,
    assigneeOptions,
    conversation.urgency,
    conversation.conversationStatusId,
    conversation.conversationId,
    conversation.partnerId,
    conversation.partner?.id,
    conversation.type,
    urgencyOptions,
    conversationParticipantOption,
  ]);

  return { conversationPropertiesFormFields, updateConversation };
}

function getDefaultConversationParticipantOption(
  input: GetDefaultConversationParticipantOptionInput,
): PreparedConversationParticipantOption | null {
  if (!input.participantIdentity) return null;

  let label = input.fullName?.trim() || '';

  if (
    input.participantEntity === ConversationParticipantEntity.PlatformUser &&
    !input.requester?.platformUser?.isActive
  ) {
    label = appendDisabledUserTextToName(label);
  }

  return {
    id: input.participantIdentity ?? '',
    value: input.participantIdentity ?? '',
    label,
    group: getPreparedConversationParticipantOptionGroup(input.participantEntity!),
    participantEntity: input.participantEntity || ConversationParticipantEntity.Client,
    conversationParticipantId: input.conversationParticipantId ?? '',
    phoneNumber: input.phoneNumber ?? '',
    email: input.email ?? '',
    entityPartnerId: input.requester?.platformUser?.partnerId,
    avatarFileKey: input.requester?.platformUser?.avatarFileKey ?? null,
  };
}
